import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LocaleContext } from "./LocaleContext.js";

const LOCALE_KEY = "ag_locale";
const DEFAULT_LOCALE = "en";

export function LocaleProvider({ children }) {
  const [pack, setPack] = useState(null);
  const [locale, setLocaleState] = useState(() => {
    try {
      return localStorage.getItem(LOCALE_KEY) || DEFAULT_LOCALE;
    } catch {
      return DEFAULT_LOCALE;
    }
  });

  useEffect(() => {
    let cancelled = false;
    fetch("/lang.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled) setPack(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const setLocale = useCallback((k) => {
    setLocaleState(k);
    try {
      localStorage.setItem(LOCALE_KEY, k);
    } catch {}
  }, []);

  const availableLocales = useMemo(() => {
    if (!pack) return [DEFAULT_LOCALE];
    const first = pack.langId && Object.values(pack.langId)[0];
    if (first?.values) return Object.keys(first.values);
    return [DEFAULT_LOCALE];
  }, [pack]);

  const t = useCallback(
    (id, fallback = "") => {
      if (!pack) return fallback;
      const entry = pack.langId?.[id];
      if (!entry?.values) return fallback;
      return entry.values[locale] ?? entry.values[DEFAULT_LOCALE] ?? fallback;
    },
    [pack, locale]
  );

  const value = useMemo(
    () => ({ locale, setLocale, availableLocales, t, ready: !!pack }),
    [locale, setLocale, availableLocales, t, pack]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
